import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import Cookies from 'js-cookie';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import RatioCalculator from '../components/RatioCalculator';
import MultiMonthComparison from '../components/MultiMonthComparison';
import { fetchMultiMonthAnalytics } from '../utils/multiMonthAnalytics';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const API_BASE_URL = 'https://system.sales-management.cloud/api';
const DAILY_REPORT_API = `${API_BASE_URL}/daily_report.php`;

function Analytics() {
  const [ loading, setLoading ] = useState(true);
  const [ error, setError ] = useState('');
  const [ period, setPeriod ] = useState('this_month');
  const [ customFrom, setCustomFrom ] = useState('');
  const [ customTo, setCustomTo ] = useState('');
  const [ data, setData ] = useState(null);
  const [ selectedMetric, setSelectedMetric ] = useState('');
  const [ multiMonthData, setMultiMonthData ] = useState(null);

  // 認証データ取得
  const getAuthData = useCallback(() => {
    const token = Cookies.get('auth_token');
    const userInfoStr = Cookies.get('user_info');

    if (!token || !userInfoStr) {
      return null;
    }

    try {
      const userInfo = JSON.parse(userInfoStr);
      return { token, user: userInfo };
    } catch (error) {
      console.error('Cookie parse error:', error);
      return null;
    }
  }, []);

  // データ取得
  const fetchAnalyticsData = useCallback(async () => {
    const auth = getAuthData();

    if (!auth) {
      setError('ログインが必要です');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const periodInfo = getPeriodRange(period, customFrom, customTo);

      if (!periodInfo) {
        setError('開始日と終了日を指定してください');
        setLoading(false);
        return;
      }

      const url = `${DAILY_REPORT_API}?start_date=${periodInfo.from}&end_date=${periodInfo.to}&limit=100`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${auth.token}`,
        },
      });

      const result = await response.json();


      if (result.status === 'success') {
        const analyticsData = buildAnalyticsData(result.data?.reports || [], periodInfo, auth.user);
        setData(analyticsData);
        setSelectedMetric(analyticsData.metrics[0] || '');
        setError('');
      } else {
        setError(result.message || 'データ取得に失敗しました');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('分析データの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [ period, customFrom, customTo, getAuthData ]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [ fetchAnalyticsData ]);

  const resolveComparisonBaseDate = useCallback(() => {
    const periodInfo = getPeriodRange(period, customFrom, customTo);
    if (!periodInfo) {
      return null;
    }

    if (period === 'last_month' || period === 'two_months_ago') {
      return periodInfo.from;
    }

    if (period === 'custom') {
      return periodInfo.to || periodInfo.from;
    }

    return moment().format('YYYY-MM-DD');
  }, [period, customFrom, customTo]);

  // 複数月データ取得
  const fetchMultiMonthData = useCallback(async () => {
    const auth = getAuthData();
    const baseDate = resolveComparisonBaseDate();
    
    if (!auth || !baseDate) {
      setMultiMonthData(null);
      return;
    }

    try {
      const result = await fetchMultiMonthAnalytics(auth.token, DAILY_REPORT_API, null, baseDate);
      setMultiMonthData(result);
    } catch (err) {
      console.error('Failed to fetch multi-month data:', err);
      setMultiMonthData(null);
    }
  }, [ getAuthData, resolveComparisonBaseDate ]);

  useEffect(() => {
    fetchMultiMonthData();
  }, [ fetchMultiMonthData ]);

  // 日別推移グラフデータ生成
  const generateChartData = useCallback(() => {
    if (!data?.period?.from || !data?.period?.to || data.metrics.length === 0 || !selectedMetric) return null;

    const metricLabels = data.metrics.reduce((acc, key) => {
      acc[ key ] = key;
      return acc;
    }, {});

    // 期間の開始日から終了日までの全日付を生成
    const startDate = moment(data.period.from);
    const endDate = moment(data.period.to);
    const allDates = [];
    const labels = [];

    let currentDate = startDate.clone();
    while (currentDate.isSameOrBefore(endDate, 'day')) {
      allDates.push(currentDate.format('YYYY-MM-DD'));
      labels.push(currentDate.format('D'));
      currentDate.add(1, 'day');
    }

    // 日別データをマップに変換
    const dailyDataMap = {};
    if (data.daily_data) {
      data.daily_data.forEach(d => {
        dailyDataMap[ d.date ] = d;
      });
    }

    // 全日付に対してデータを生成（データがない日はnull）
    const dataValues = allDates.map(date => {
      const dayData = dailyDataMap[ date ];
      if (!dayData) return null;

      return dayData[ selectedMetric ] || 0;
    });

    return {
      labels,
      datasets: [ {
        label: metricLabels[ selectedMetric ],
        data: dataValues,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        tension: 0.3,
        spanGaps: false,
      } ],
      metricOptions: data.metrics.map(metric => ({
        value: metric,
        label: metric
      }))
    };
  }, [ data, selectedMetric ]);

  // 達成率グラフデータ生成
  const generateAchievementChart = useCallback(() => null, []);
  const generateConversionChart = useCallback(() => null, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <Header />

      <PeriodSelector
        period={period}
        setPeriod={setPeriod}
        customFrom={customFrom}
        setCustomFrom={setCustomFrom}
        customTo={customTo}
        setCustomTo={setCustomTo}
        onUpdate={fetchAnalyticsData}
      />

      {error && <ErrorMessage message={error} />}

      {data && (
        <>
          <UserInfo data={data} />

          <SummaryCards data={data} />

          {multiMonthData && (
            <MultiMonthComparison multiMonthData={multiMonthData} />
          )}

          <DailyTrendChart
            selectedMetric={selectedMetric}
            setSelectedMetric={setSelectedMetric}
            chartData={generateChartData()}
          />

          <RatioCalculator
            metrics={data.metrics}
            summary={data.summary}
            daily_data={data.daily_data}
            targets={data.targets}
            target_items={data.target_items}
          />

          <AchievementCharts
            achievementData={generateAchievementChart()}
            conversionData={generateConversionChart()}
          />
        </>
      )}
    </div>
  );
}

// ヘッダー
const Header = () => (
  <div className="mb-6">
    <h1 className="text-2xl md:text-3xl font-bold text-gray-900">分析ダッシュボード</h1>
  </div>
);

// 期間選択
const PeriodSelector = ({ period, setPeriod, customFrom, setCustomFrom, customTo, setCustomTo, onUpdate }) => (
  <div className="bg-white rounded-lg shadow p-4 mb-4">
    <div className="flex flex-col md:flex-row gap-4">
      <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 mb-2">期間</label>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="this_month">今月</option>
          <option value="last_month">先月</option>
          <option value="two_months_ago">先々月</option>
          <option value="custom">カスタム期間</option>
        </select>
      </div>

      {period === 'custom' && (
        <>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">開始日</label>
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">終了日</label>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </>
      )}

      <div className="flex items-end">
        <button
          onClick={onUpdate}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm md:text-base"
        >
          更新
        </button>
      </div>
    </div>
  </div>
);

// エラーメッセージ
const ErrorMessage = ({ message }) => (
  <div className="mb-4 p-3 md:p-4 bg-red-50 border border-red-200 rounded-md">
    <p className="text-xs md:text-sm text-red-600">{message}</p>
  </div>
);

// ユーザー情報
const UserInfo = ({ data }) => (
  <div className="bg-white rounded-lg shadow p-4 mb-6">
    <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-2">{data.user?.name || 'ユーザー'}</h2>
    <p className="text-xs md:text-sm text-gray-600">
      期間: {data.period?.label} ({data.period?.from} 〜 {data.period?.to})
    </p>
    <p className="text-xs md:text-sm text-gray-600">稼働日数: {data.summary?.working_days || 0}日</p>
  </div>
);

// サマリーカード群
const SummaryCards = ({ data }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
    {data.metrics.map((metric, index) => (
      <SummaryCard
        key={metric}
        title={metric}
        value={data.summary?.[ metric ] || 0}
        color={pickSummaryColor(index)}
      />
    ))}
  </div>
);

// サマリーカード
const SummaryCard = ({ title, value, target, rate, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-600',
    green: 'bg-green-50 border-green-200 text-green-600',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-600',
    red: 'bg-red-50 border-red-200 text-red-600',
  };

  return (
    <div className={`rounded-lg border-2 p-3 md:p-4 ${colorClasses[ color ]}`}>
      <h3 className="text-xs md:text-sm font-medium mb-1 md:mb-2">{title}</h3>
      <p className="text-xl md:text-3xl font-bold mb-1">{value}</p>
      {target && (
        <p className="text-[10px] md:text-xs opacity-75">目標: {target}</p>
      )}
      {rate !== undefined && rate !== null && (
        <p className="text-[10px] md:text-xs font-semibold mt-1 md:mt-2">達成率: {rate.toFixed(1)}%</p>
      )}
    </div>
  );
};

// 日別推移グラフ
const DailyTrendChart = ({ selectedMetric, setSelectedMetric, chartData }) => {
  if (!chartData) return null;

  return (
    <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
        <h3 className="text-base md:text-lg font-semibold text-gray-900">日別推移</h3>
        <select
          value={selectedMetric}
          onChange={(e) => setSelectedMetric(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-xs md:text-sm"
        >
          {chartData.metricOptions?.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
      <div className="h-64 md:h-80">
        <Line
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: true, position: 'top' },
            },
            scales: {
              y: { beginAtZero: true },
            },
          }}
        />
      </div>
    </div>
  );
};

// 達成率・転換率グラフ
const AchievementCharts = () => null;

export default Analytics;

const getPeriodRange = (period, customFrom, customTo) => {
  const today = moment();

  if (period === 'custom') {
    if (!customFrom || !customTo) return null;
    return { from: customFrom, to: customTo, label: 'カスタム期間' };
  }

  if (period === 'this_month') {
    return {
      from: today.clone().startOf('month').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD'),
      label: '今月',
    };
  }

  if (period === 'last_month') {
    const lastMonth = today.clone().subtract(1, 'month');
    return {
      from: lastMonth.startOf('month').format('YYYY-MM-DD'),
      to: lastMonth.endOf('month').format('YYYY-MM-DD'),
      label: '先月',
    };
  }

  if (period === 'two_months_ago') {
    const target = today.clone().subtract(2, 'month');
    return {
      from: target.startOf('month').format('YYYY-MM-DD'),
      to: target.endOf('month').format('YYYY-MM-DD'),
      label: '先々月',
    };
  }

  return null;
};

const buildAnalyticsData = (reports, periodInfo, user) => {
  const metricsSet = new Set();
  const summary = {};
  const dailyMap = {};
  const workingDays = new Set();

  reports.forEach(report => {
    const date = report.work_date;
    if (date) workingDays.add(date);

    const content = report.daily_report_content || {};
    Object.entries(content).forEach(([ key, value ]) => {
      if (!isNumericValue(value)) return;
      const num = Number(value);
      if (!Number.isFinite(num)) return;

      metricsSet.add(key);
      summary[ key ] = (summary[ key ] || 0) + num;

      if (!dailyMap[ date ]) {
        dailyMap[ date ] = { date };
      }
      dailyMap[ date ][ key ] = (dailyMap[ date ][ key ] || 0) + num;
    });
  });

  const metrics = Array.from(metricsSet);
  const dailyData = Object.values(dailyMap).sort((a, b) => a.date.localeCompare(b.date));

  return {
    user: user ? { name: user.name } : undefined,
    period: periodInfo,
    metrics,
    summary: { ...summary, working_days: workingDays.size },
    daily_data: dailyData,
  };
};

const isNumericValue = (value) => {
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value !== 'string') return false;
  return /^-?\d+(\.\d+)?$/.test(value.trim());
};

const pickSummaryColor = (index) => {
  const colors = [ 'blue', 'green', 'yellow', 'red' ];
  return colors[ index % colors.length ];
};